/**
 * Search panel (M4.6, rebuilt for v2 in RET-256): search opens in place.
 * Loaded only when Theme settings, Launch features, "Search opens in place" is on
 * (sections/header.liquid). The header icon stays a real link to /search: without this
 * script, or if showModal is missing, it navigates as before.
 *
 * Results come from sections/predictive-search.liquid through the Section Rendering API
 * (resources[type] query, product, collection, page). One request in flight, stale responses
 * dropped, typing debounced. The menu's categories and the brands are listed before Shopify's
 * collections; they and the idle best sellers are not in the page: the panel fetches them the
 * first time it opens (sections/search-catalog.liquid, and the best-sellers collection through
 * sections/predictive-search.liquid), so pages that never open it do none of that work. With Did you mean for brands on, a query that nothing
 * really matches is compared with the brand names (assets/v2-did-you-mean.js).
 */
const SEARCH_PHONE = '(max-width: 749px)';
const MAX_CATEGORIES = 6;

const foldWords = (text) =>
  String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

// Brands without a collection all sit on /collections/vendors?q=<name>: the query string
// tells them apart, so the dedupe keeps every one.
const pathOf = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    const path = parsed.pathname.replace(/\/$/, '');
    return path === '/collections/vendors' || path === '/collections/types' ? path + parsed.search : path;
  } catch (error) {
    return url;
  }
};

class SearchDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.idle = this.querySelector('[data-search-idle]');
    this.status = this.querySelector('[data-search-status]');
    this.clearButton = this.querySelector('[data-search-clear]');
    if (!this.dialog || !this.input || typeof this.dialog.showModal !== 'function') return;

    this.strings = this.#decode(this.#readJson(this, '[data-search-strings]', {}));
    this.catalog = [];
    this.placeholder = this.input.getAttribute('placeholder') || '';

    // Theme editor re-renders reconnect the element: drop the old document listeners.
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-search-open]');
      if (!opener) return;
      event.preventDefault();
      this.open(opener);
    }, { signal });

    // Warm the lists when the shopper heads for Search, so they are ready on open.
    const warm = (event) => {
      if (event.target.closest?.('[data-search-open]')) this.#loadData();
    };
    document.addEventListener('pointerover', warm, { signal, passive: true });
    document.addEventListener('focusin', warm, { signal });

    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.#close(), { signal });
    this.clearButton?.addEventListener('click', () => this.#clear(), { signal });
    // Backdrop: the dialog itself is the target when the click misses the panel.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.#close();
    }, { signal });
    // Close requests other than the Escape key (#onKeydown takes that), such as Android's
    // back gesture, fade the panel out too. One the browser will not let the page hold
    // closes at once.
    this.dialog.addEventListener('cancel', (event) => {
      if (!event.cancelable) return;
      event.preventDefault();
      this.#close();
    }, { signal });
    this.dialog.addEventListener('close', () => this.#onClose(), { signal });

    this.input.addEventListener('input', () => {
      this.#syncClear();
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.#search(), 250);
    }, { signal });

    this.dialog.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    window.addEventListener('resize', () => {
      if (this.dialog.open) this.#place();
    }, { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.controller?.abort();
  }

  open(trigger) {
    // Opening while the panel fades out keeps it open.
    if (this.dialog.open) {
      window.DialogMotion?.cancel(this.dialog);
      return;
    }
    this.trigger = trigger || null;
    // The phone menu has no search icon, but never stack two modals.
    const mobileNav = document.getElementById('MobileNav');
    if (mobileNav?.open) mobileNav.close();
    this.#loadData();
    this.#place();
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    this.#setExpanded(true);
    this.#syncClear();
    this.input.focus();
    this.input.select();
  }

  /* The panel opens under the header (which stays clear of the dim) and the phone
     placeholder is the short one. */
  #place() {
    const header = document.querySelector('.header-section') || document.querySelector('.header');
    const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.dialog.style.setProperty('--search-top', `${top}px`);
    const phone = window.matchMedia(SEARCH_PHONE).matches;
    this.input.setAttribute('placeholder', phone ? this.input.dataset.placeholderShort || this.placeholder : this.placeholder);
  }

  #setExpanded(value) {
    document.querySelectorAll('[data-search-open]').forEach((opener) => {
      opener.setAttribute('aria-expanded', value ? 'true' : 'false');
    });
  }

  /* The panel fades out before the dialog closes (window.DialogMotion, assets/theme.js,
     which loads after this file); without that script it closes at once. Either way the
     close event runs #onClose. */
  #close() {
    if (window.DialogMotion) window.DialogMotion.close(this.dialog);
    else this.dialog.close();
  }

  #onClose() {
    document.body.style.overflow = '';
    clearTimeout(this.timer);
    this.controller?.abort();
    this.#setExpanded(false);
    if (this.trigger && this.trigger.isConnected) this.trigger.focus();
  }

  #syncClear() {
    if (this.clearButton) this.clearButton.hidden = this.input.value === '';
  }

  #clear() {
    this.input.value = '';
    this.#syncClear();
    clearTimeout(this.timer);
    this.#showIdle();
    this.input.focus();
  }

  /* The t filter HTML-escapes plain translations (a quote arrives as &quot;), and the script
     sets these strings as text: turn the entities back into characters once. */
  #decode(value) {
    if (typeof value === 'string') {
      return value.includes('&') ? new DOMParser().parseFromString(value, 'text/html').documentElement.textContent : value;
    }
    if (Array.isArray(value)) return value.map((item) => this.#decode(item));
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, this.#decode(item)]));
    }
    return value;
  }

  #readJson(root, selector, fallback) {
    const el = root.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return fallback;
    }
  }

  /* The first open (or hover and focus on the header icon) fetches what the panel needs
     beyond its shell. Each request runs once; a failed one is tried again next time. */
  #loadData() {
    this.#loadCatalog();
    this.#loadIdle();
  }

  /* Menu categories (every depth) then brands, each with its title's words. */
  #loadCatalog() {
    if (this.catalogPromise) return this.catalogPromise;
    const url = this.dataset.catalogUrl;
    if (!url) return Promise.resolve();
    this.catalogPromise = fetch(url, { headers: { Accept: 'text/html' } })
      .then((res) => {
        if (!res.ok) throw new Error(`Search catalog ${res.status}`);
        return res.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const entries = [];
        this.#readJson(doc, '[data-search-catalog]', []).forEach((item) => {
          if (item && item.t && item.u) entries.push({ title: item.t, url: item.u });
        });
        const brands = this.#readJson(doc, '[data-v2-brands]', []);
        brands.forEach((item) => {
          if (item && item.n && item.u) entries.push({ title: item.n, url: item.u });
        });
        // Did you mean compares with the same brands.
        window.FlushDidYouMean?.setBrands(brands);
        this.catalog = entries.map((entry) => ({ ...entry, words: foldWords(entry.title), path: pathOf(entry.url) }));
      })
      .catch(() => {
        this.catalogPromise = null;
      });
    return this.catalogPromise;
  }

  /* The four best sellers under the popular searches. */
  #loadIdle() {
    if (this.idlePromise) return this.idlePromise;
    const wrap = this.querySelector('[data-search-idle-best]');
    const target = wrap?.querySelector('[data-search-idle-cards]');
    const url = this.dataset.idleUrl;
    if (!wrap || !target || !url) return Promise.resolve();
    this.idlePromise = fetch(url, { headers: { Accept: 'text/html' } })
      .then((res) => {
        if (res.status === 404) return '';
        if (!res.ok) throw new Error(`Search best sellers ${res.status}`);
        return res.text();
      })
      .then((text) => {
        const list = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-brand-picks] ul');
        // No collection or no products: the group stays hidden.
        if (!list || !list.children.length) return;
        const node = document.importNode(list, true);
        // The idle cards are a grid on phones too, not the result rows.
        node.classList.remove('v2-search__cards--rows');
        this.#scopeIds(node, 'SearchIdle');
        target.replaceChildren(node);
        wrap.hidden = false;
      })
      .catch(() => {
        this.idlePromise = null;
      });
    return this.idlePromise;
  }

  /* Every query word starts a word of the title, case and accents ignored. */
  #localMatches(query) {
    const queryWords = foldWords(query);
    if (!queryWords.length) return [];
    const seen = new Set();
    const found = [];
    for (const entry of this.catalog) {
      if (seen.has(entry.path)) continue;
      if (queryWords.every((w) => entry.words.some((t) => t.startsWith(w)))) {
        seen.add(entry.path);
        found.push(entry);
      }
    }
    return found;
  }

  /* Visible result links, in source order, for the arrow keys. */
  #links() {
    const scope = this.idle && !this.idle.hidden ? this.idle : this.results;
    return [...scope.querySelectorAll('[data-search-result], .search-chips__chip, .product-card__link')]
      .filter((el) => el.getClientRects().length > 0);
  }

  /* Escape closes the panel on the first press: in a search field the browser would spend
     it clearing the text and swallow the dialog's own close. Arrow Down from the input
     goes to the first result; Up and Down move between result links; Up from the first
     returns to the input. */
  #onKeydown(event) {
    if (event.key === 'Escape' && !event.isComposing) {
      event.preventDefault();
      this.#close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const links = this.#links();
    if (!links.length) return;
    const index = links.indexOf(document.activeElement);
    if (document.activeElement === this.input) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        links[0].focus();
      }
      return;
    }
    if (index === -1) return;
    event.preventDefault();
    if (event.key === 'ArrowDown') links[Math.min(index + 1, links.length - 1)].focus();
    else if (index === 0) this.input.focus();
    else links[index - 1].focus();
  }

  #showIdle() {
    this.controller?.abort();
    this.results.replaceChildren();
    this.results.removeAttribute('aria-busy');
    if (this.idle) this.idle.hidden = false;
    this.status.textContent = '';
  }

  /* Old results stay in place until the new ones are ready, so nothing jumps while typing. */
  #show(node, statusText) {
    if (this.idle) this.idle.hidden = true;
    this.results.replaceChildren(node);
    this.results.removeAttribute('aria-busy');
    this.status.textContent = statusText || '';
  }

  async #search() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.#showIdle();
      return;
    }
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.results.setAttribute('aria-busy', 'true');
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'query,product,collection,page',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });
    try {
      const res = await fetch(`${this.dataset.url}?${params.toString()}`, { signal: controller.signal });
      if (!res.ok) throw new Error(`Predictive search ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const root = doc.querySelector('[data-predictive-results]');
      if (!root) throw new Error('Empty predictive search response');
      // The menu and brand lists come with the first open; wait for them if they are late.
      await this.#loadCatalog();
      if (controller.signal.aborted || this.input.value.trim() !== query) return;
      const local = this.#localMatches(query);

      const brand = this.#didYouMean(query, root, local);
      if (brand) {
        await this.#showDidYouMean(query, brand, controller);
        return;
      }

      const node = document.importNode(root, true);
      this.#scopeIds(node, 'Search');
      const categoryCount = this.#mergeCategories(node, local);
      this.#show(node, this.#statusFor(node, categoryCount, query));
    } catch (error) {
      if (error.name === 'AbortError' || this.input.value.trim() !== query) return;
      this.#showError(query);
    }
  }

  /* Product cards can repeat a card already on the page: prefix the ids in a fetched
     fragment and every reference to them, so ids stay unique. */
  #scopeIds(root, prefix) {
    const ids = new Map();
    root.querySelectorAll('[id]').forEach((el) => {
      ids.set(el.id, `${prefix}${el.id}`);
      el.id = `${prefix}${el.id}`;
    });
    if (!ids.size) return;
    const attrs = ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'];
    root.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], label[for]').forEach((el) => {
      attrs.forEach((attr) => {
        const value = el.getAttribute(attr);
        if (value) el.setAttribute(attr, value.split(/\s+/).map((id) => ids.get(id) || id).join(' '));
      });
    });
  }

  /* Menu categories and brands first, then Shopify's collections without duplicates. */
  #mergeCategories(node, local) {
    const list = node.querySelector('[data-search-categories]');
    if (!list) return 0;
    const shopify = [...list.children];
    const seen = new Set();
    const items = [];
    local.forEach((entry) => {
      seen.add(entry.path);
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'v2-search__link';
      link.href = entry.url;
      link.textContent = entry.title;
      link.setAttribute('data-search-result', '');
      li.append(link);
      items.push(li);
    });
    shopify.forEach((li) => {
      const path = pathOf(li.dataset.path || li.querySelector('a')?.getAttribute('href') || '');
      if (seen.has(path)) return;
      seen.add(path);
      items.push(li);
    });
    const shown = items.slice(0, MAX_CATEGORIES);
    list.replaceChildren(...shown);
    const group = list.closest('[data-search-group="categories"]');
    if (group) group.hidden = shown.length === 0;
    return shown.length;
  }

  #plural(pair, count) {
    if (!Array.isArray(pair)) return '';
    return String(count === 1 ? pair[0] : pair[1]).replace('[count]', count);
  }

  /* One polite sentence: counts of what is shown, or the no-match line. */
  #statusFor(node, categoryCount, query) {
    if (node.dataset.match !== 'strong') {
      const parts = [String(this.strings.noMatches || '').replace('[terms]', () => query)];
      if (categoryCount) parts.push(this.#plural(this.strings.status?.categories, categoryCount));
      return parts.join(', ');
    }
    const count = (selector) => node.querySelectorAll(selector).length;
    const status = this.strings.status || {};
    const parts = [];
    const suggestions = count('[data-search-group="suggestions"] li');
    const products = count('[data-search-group="products"] li');
    const pages = count('[data-search-group="pages"] li');
    if (suggestions) parts.push(this.#plural(status.suggestions, suggestions));
    if (categoryCount) parts.push(this.#plural(status.categories, categoryCount));
    if (products) parts.push(this.#plural(status.products, products));
    if (pages) parts.push(this.#plural(status.pages, pages));
    return parts.join(', ');
  }

  /* Offer a brand only when nothing really matches: the theme's match check is weak, or the
     query's words are not all in the results, and no brand or category already starts with
     the query. */
  #didYouMean(query, root, local) {
    if (!this.hasAttribute('data-did-you-mean') || !window.FlushDidYouMean) return null;
    const dym = window.FlushDidYouMean;
    const brand = dym.match(query);
    if (!brand) return null;
    if (local.length) return null;
    const weak = root.dataset.match !== 'strong';
    if (!weak && dym.wordsFound(query, root.dataset.hay || '')) return null;
    return brand;
  }

  async #showDidYouMean(query, brand, controller) {
    const dym = window.FlushDidYouMean;
    const strings = this.strings;
    const wrap = document.createElement('div');
    wrap.className = 'v2-search-results v2-search-layout v2-dym';

    const side = document.createElement('div');
    side.className = 'v2-search-layout__side';
    const none = document.createElement('p');
    none.className = 'v2-search__none v2-search__none--quiet';
    none.textContent = String(strings.noExact || '').replace('[terms]', () => query);
    const heading = document.createElement('h3');
    heading.className = 'v2-dym__title';
    const [before, after = ''] = dym.fill(strings.didYouMean, '\u0000').split('\u0000');
    const brandLink = document.createElement('a');
    brandLink.className = 'v2-dym__brand';
    brandLink.href = brand.url;
    brandLink.textContent = brand.name;
    brandLink.setAttribute('data-search-result', '');
    heading.append(before, brandLink, after);
    side.append(none, heading);

    const main = document.createElement('div');
    main.className = 'v2-search-layout__main v2-dym__picks';
    const shopAll = document.createElement('a');
    shopAll.className = 'btn btn--outline v2-dym__shop-all';
    shopAll.href = brand.url;
    shopAll.textContent = dym.fill(strings.shopAllBrand, brand.name);
    shopAll.setAttribute('data-search-result', '');

    const chips = this.idle?.querySelector('.search-chips');
    const chipSide = document.createElement('div');
    chipSide.className = 'v2-search-layout__side';
    if (chips) chipSide.append(chips.cloneNode(true));

    let list = null;
    try {
      list = await dym.fetchPicks(brand.url, 'sort_by=best-selling&section_id=predictive-search', controller.signal);
    } catch (error) {
      if (error.name === 'AbortError') throw error;
    }
    if (controller.signal.aborted || this.input.value.trim() !== query) return;
    if (list && list.children.length) {
      this.#scopeIds(list, 'SearchDym');
      const label = document.createElement('h3');
      label.className = 'v2-search__heading';
      label.textContent = dym.fill(strings.brandBestSellers, brand.name);
      main.append(label, list);
    }
    main.append(shopAll);
    wrap.append(side, main, chipSide);
    this.#show(wrap, dym.fill(strings.statusDidYouMean, brand.name));
  }

  /* Never a blank panel: a "See all results" link for the typed query. */
  #showError(query) {
    const link = document.createElement('a');
    link.className = 'btn btn--outline v2-search__see-all';
    link.setAttribute('data-search-result', '');
    link.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&type=product`;
    link.textContent = String(this.strings.seeAll || '').replace('[terms]', () => query);
    const wrap = document.createElement('div');
    wrap.className = 'v2-search-results';
    wrap.append(link);
    this.#show(wrap, this.strings.unavailable || '');
  }
}

if (!customElements.get('search-dialog')) {
  customElements.define('search-dialog', SearchDialog);
}
